// Firebase modules are now dynamically imported to eliminate blocking initial page paint.
async function getFirestoreService() {
  const { getFirebaseServices } = await import("./firebase");
  const { db, isConfigured } = await getFirebaseServices();
  if (!isConfigured || !db) {
    throw new Error("Database not connected or configured");
  }
  const firestore = await import("firebase/firestore");
  return { db, ...firestore };
}

async function getStorageService() {
  const { getFirebaseServices } = await import("./firebase");
  const { storage } = await getFirebaseServices();
  if (!storage) {
    throw new Error("Storage not configured");
  }
  const storageLib = await import("firebase/storage");
  return { storage, ...storageLib };
}
import type { Product, Combo, HeroContent, PromoContent, CommitmentContent, Review, AboutContent } from "@/types/content";

export const DEFAULT_PRODUCTS: Product[] = [
  {
    name: "Daily Demand® Premium Bamboo Diapers",
    description:
      "100% safe and gentle for your baby's sensitive skin. Features 12-hour dry-lock core for maximum comfort. Chemical-free and dermatologist tested.",
    price: 1250,
    image: "/product-signature.webp",
    tag: "Best Seller",
    order: 0,
  },
  {
    name: "Daily Demand® Pure Baby Wipes",
    description:
      "Made with 99% pure water with Aloe Vera and Chamomile extracts. Extra soft, fragrance-free, and perfectly safe for newborns.",
    price: 450,
    image: "/product-classic.webp",
    tag: "Halal & Safe",
    order: 1,
  },
  {
    name: "Daily Demand® Food-Grade Spoon Feeder",
    description:
      "100% Food-Grade and BPA-free silicone feeding set. Perfect for liquids, soups, and ORS. Features a soft squeezable body with a clear measurement scale.",
    price: 850,
    image: "https://i.ibb.co.com/wZ43S7Pm/7.png",
    tag: "100% Food Grade",
    order: 2,
  },
  {
    name: "Daily Demand® Premium Cotton Buds",
    description:
      "Versatile care for your baby's navel, beauty correction, first aid, and cleaning delicate electronics. Soft and safe for various uses.",
    price: 250,
    image: "https://i.ibb.co.com/QhN0zYP/1.png",
    tag: "Multi-Purpose",
    order: 3,
  },
];

export const DEFAULT_COMBOS: Combo[] = [

  {
    name: "Daily Essential Starter Kit",
    items: [
      "Premium Bamboo Diapers (30 Pcs)",
      "Pure Baby Wipes (80 Pcs)",
      "Food-Grade Feeding Spoon",
    ],
    price: 1899,
    originalPrice: 2500,
    badge: "Save 24%",
    image: "/combo-pack.webp",
    order: 0,
  },
  {
    name: "Premium Hygiene Bundle",
    items: [
      "Overnight Comfort Pants (20 Pcs)",
      "Pure Baby Wipes (160 Pcs)",
      "Bamboo Diapers (30 Pcs)",
      "Food-Grade Silicone Bib",
    ],
    price: 3499,
    originalPrice: 4500,
    badge: "Save 22%",
    image: "/combo-pack.webp",
    order: 1,
  },
];

export const DEFAULT_HERO: HeroContent = {
  titleLine1: "Nothing is more",
  titleHighlight: "Precious Than",
  titleLine2: "Your Child's Smile",
  subtitle:
    "Daily Demand is committed to honoring your love. We ensure international quality, 100% Food Grade, and Halal products delivered to your doorstep like a family member.",
  heroImageUrls: [
    "https://i.ibb.co/svLDRZs9/1.png",
    "https://i.ibb.co/JwcrbbCv/2.png",
    "https://i.ibb.co/xSrtYzf5/4.png"
  ],
  ctaText: "Explore Collection",
};

export const DEFAULT_PROMO: PromoContent = {
  badge: "🎁 Special Offer",
  titleLine1: "Free Delivery",
  titleHighlight: "On Orders Over",
  titleLine2: "৳2000",
  subtitle: "Order today and receive your baby essentials right at your doorstep — with completely free delivery. Applicable for inside Dhaka only.",
  ctaText: "Shop Now",
  bgImageUrl: "/promo-banner.webp",
  items: [
    { icon: "🍃", title: "Bamboo Diapers", sub: "Extra soft & safe", price: "৳1,250" },
    { icon: "💧", title: "Baby Wipes", sub: "99% Pure Water", price: "৳450" },
    { icon: "🥄", title: "Feeding Set", sub: "Food-Grade Silicone", price: "৳1,550" },
    { icon: "🌙", title: "Overnight Pants", sub: "12H Protection", price: "৳1,350" },
  ]
};

export const DEFAULT_COMMITMENT: CommitmentContent = {
  badge: "OUR COMMITMENT",
  titleLine1: "TRUSTED BY",
  titleHighlight: "10,000+",
  titleLine2: "BANGLADESHI FAMILIES",
  description: "From Dhaka to Chittagong, Daily Demand is the first choice of countless parents. The reason is simple — we never compromise on child safety. Every product reaches you after strict quality control.",
  bgImageUrl: "/size-up-banner.webp",
  stats: [
    { value: "10,000+", label: "Happy Families" },
    { value: "100%", label: "Chemical Free" },
    { value: "4.9★", label: "Average Rating" },
  ],
  deliveryTitle: "Fast Delivery",
  deliverySub: "Dhaka: 24 Hours",
  paymentTitle: "Cash on Delivery",
  paymentSub: "Pay after receiving"
};

export const DEFAULT_REVIEWS: Review[] = [
  {
    name: "Sumaiya Akter",
    content: "Outstanding quality! Baby stays dry all night.",
    image: ""
  },
  {
    name: "Rahat Hossain",
    content: "Fast delivery and authentic products. 5 stars!",
    image: ""
  },
  {
    name: "Nusrat Jahan",
    content: "Safe and hygienic. Highly recommended for moms.",
    image: ""
  }
];

export const DEFAULT_ABOUT: AboutContent = {
  hero: {
    line1: "BY PARENTS",
    highlight: "WITH LOVE,",
    line2: "FOR PARENTS.",
    description: "In 2021, a father in Dhaka couldn't find a safe and affordable diaper for his newborn daughter. So he created the brand he felt was missing.",
    bgImage: "/size-up-banner.webp"
  },
  ticker: ["Safe for Newborns", "Dermatologist Tested", "100% Chemical Free", "Made with Love", "Food-Grade Materials", "Bangladesh's Trusted Brand"],
  origin: {
    year: "2021",
    title: "A Father's Commitment.",
    p1: "Rafiqul Islam had everything to be happy — except safe baby products. Foreign products in the market were unaffordable, and local products contained harmful chemicals.",
    p2: "So he left his corporate job, found a food-grade material supplier, and after 6 months of hard work, created Daily Demand's first bamboo diaper.",
    p3: "Four years later, more than 10,000 families trust our same commitment in every product.",
    quote: "If it's not safe for my daughter, our brand name won't be on it.",
    quoteAuthor: "RAFIQUL ISLAM, FOUNDER",
    image: "/product-signature.webp"
  },
  stats: {
    families: 10000,
    districts: 64,
    safetyChecks: 27,
    years: 4
  },
  values: [
    { title: "No Compromise on Safety", desc: "Every one of our products goes through 27 safety checks." },
    { title: "Transparency over Marketing", desc: "We publish the list of every ingredient." },
    { title: "Best Quality within Reach", desc: "Child safety shouldn't be a luxury." },
    { title: "Caring for the Environment", desc: "Sustainable bamboo and plant-based wipes." }
  ],
  promise: {
    title: "We only create products that we can give to our own children without hesitation.",
    bgImage: "/promo-banner.webp"
  }
};

/* ——— Caching Helpers for Stale-While-Revalidate ——— */
function getCached<T>(key: string, defaultValue: T): T {
  try {
    const cached = localStorage.getItem(`cache_${key}`);
    return cached ? JSON.parse(cached) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function setCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(`cache_${key}`, JSON.stringify(data));
  } catch (err) {
    console.error("Cache set failed:", err);
  }
}

function clearCache(key: string): void {
  try {
    localStorage.removeItem(`cache_${key}`);
  } catch (err) {
    console.error("Cache clear failed:", err);
  }
}

// ——— Firestore read ———
export async function getProducts(onUpdate?: (products: Product[]) => void): Promise<Product[]> {
  const cached = getCached("products", DEFAULT_PRODUCTS);
  setTimeout(async () => {
    try {
      const { db, collection, getDocs } = await getFirestoreService();
      const snap = await getDocs(collection(db, "products"));
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Product));
        list.sort((a, b) => {
          const aIsUpcoming = a.tag?.trim().toLowerCase() === 'upcoming';
          const bIsUpcoming = b.tag?.trim().toLowerCase() === 'upcoming';
          if (aIsUpcoming && !bIsUpcoming) return 1;
          if (!aIsUpcoming && bIsUpcoming) return -1;
          return (a.order ?? 0) - (b.order ?? 0);
        });
        setCache("products", list);
        if (onUpdate) onUpdate(list);
      }
    } catch (err) {
      console.warn("Firestore getProducts failed:", err);
    }
  }, 0);
  return cached;
}

export async function getCombos(onUpdate?: (combos: Combo[]) => void): Promise<Combo[]> {
  const cached = getCached("combos", DEFAULT_COMBOS);
  setTimeout(async () => {
    try {
      const { db, collection, getDocs } = await getFirestoreService();
      const snap = await getDocs(collection(db, "combos"));
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Combo));
        list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setCache("combos", list);
        if (onUpdate) onUpdate(list);
      }
    } catch (err) {
      console.warn("Firestore getCombos failed:", err);
    }
  }, 0);
  return cached;
}

export async function getHeroContent(onUpdate?: (hero: HeroContent) => void): Promise<HeroContent> {
  const cached = getCached("hero", DEFAULT_HERO);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "hero"));
      if (snap.exists()) {
        const data = snap.data();
        let urls = data.heroImageUrls || [];
        if (urls.length === 0 && data.heroImageUrl) {
          urls = [data.heroImageUrl];
        }
        const heroData = { ...DEFAULT_HERO, ...data, heroImageUrls: urls };
        setCache("hero", heroData);
        if (onUpdate) onUpdate(heroData);
      }
    } catch (err) {
      console.warn("Firestore getHeroContent failed:", err);
    }
  }, 0);
  return cached;
}

export async function getPromoContent(onUpdate?: (promo: PromoContent) => void): Promise<PromoContent> {
  const cached = getCached("promo", DEFAULT_PROMO);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "promo"));
      if (snap.exists()) {
        const promoData = { ...DEFAULT_PROMO, ...snap.data() } as PromoContent;
        setCache("promo", promoData);
        if (onUpdate) onUpdate(promoData);
      }
    } catch (err) {
      console.warn("Firestore getPromoContent failed:", err);
    }
  }, 0);
  return cached;
}

export async function getCommitmentContent(onUpdate?: (commitment: CommitmentContent) => void): Promise<CommitmentContent> {
  const cached = getCached("commitment", DEFAULT_COMMITMENT);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "commitment"));
      if (snap.exists()) {
        const commitmentData = { ...DEFAULT_COMMITMENT, ...snap.data() } as CommitmentContent;
        setCache("commitment", commitmentData);
        if (onUpdate) onUpdate(commitmentData);
      }
    } catch (err) {
      console.warn("Firestore getCommitmentContent failed:", err);
    }
  }, 0);
  return cached;
}

export async function getReviews(onUpdate?: (reviews: Review[]) => void): Promise<Review[]> {
  const cached = getCached("reviews", DEFAULT_REVIEWS);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "reviews"));
      if (snap.exists()) {
        const data = snap.data();
        const reviewsData = data.reviews || DEFAULT_REVIEWS;
        setCache("reviews", reviewsData);
        if (onUpdate) onUpdate(reviewsData);
      }
    } catch (err) {
      console.warn("Firestore getReviews failed:", err);
    }
  }, 0);
  return cached;
}

export async function getAboutContent(onUpdate?: (about: AboutContent) => void): Promise<AboutContent> {
  const cached = getCached("about", DEFAULT_ABOUT);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "about"));
      if (snap.exists()) {
        const aboutData = snap.data() as AboutContent;
        setCache("about", aboutData);
        if (onUpdate) onUpdate(aboutData);
      }
    } catch (err) {
      console.warn("Firestore getAboutContent failed:", err);
    }
  }, 0);
  return cached;
}

// ——— Firestore write (admin) ———
export async function saveProduct(product: Product): Promise<void> {
  const { db, doc, updateDoc, addDoc, collection } = await getFirestoreService();
  const { id, ...data } = product;
  const formattedData = {
    ...data,
    price: Number(data.price) || 0,
    originalPrice: data.originalPrice ? Number(data.originalPrice) : 0,
    order: Number(data.order) || 0,
  };

  try {
    if (id) {
      await updateDoc(doc(db, "products", id), formattedData as any);
    } else {
      await addDoc(collection(db, "products"), formattedData);
    }
    clearCache("products");
  } catch (err: any) {
    console.error("Firestore saveProduct failed:", err);
    throw err;
  }
}

export async function deleteProduct(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "products", id));
    clearCache("products");
  } catch (err) {
    console.error("Firestore deleteProduct failed:", err);
    throw err;
  }
}

export async function saveCombo(combo: Combo): Promise<void> {
  const { db, doc, updateDoc, addDoc, collection } = await getFirestoreService();
  const { id, ...data } = combo;
  const formattedData = {
    ...data,
    price: Number(data.price) || 0,
    originalPrice: Number(data.originalPrice) || 0,
    order: Number(data.order) || 0,
    items: Array.isArray(data.items) ? data.items.filter(i => i.trim() !== "") : [],
  };

  try {
    if (id) {
      await updateDoc(doc(db, "combos", id), formattedData as any);
    } else {
      await addDoc(collection(db, "combos"), formattedData);
    }
    clearCache("combos");
  } catch (err: any) {
    console.error("Firestore saveCombo failed:", err);
    throw err;
  }
}

export async function deleteCombo(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "combos", id));
    clearCache("combos");
  } catch (err) {
    console.error("Firestore deleteCombo failed:", err);
    throw err;
  }
}

export async function saveHeroContent(hero: HeroContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "hero"), hero);
    clearCache("hero");
  } catch (err: any) {
    console.error("Firestore saveHeroContent failed:", err);
    throw err;
  }
}

export async function savePromoContent(promo: PromoContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "promo"), promo);
    clearCache("promo");
  } catch (err: any) {
    console.error("Firestore savePromoContent failed:", err);
    throw err;
  }
}

export async function saveCommitmentContent(commitment: CommitmentContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "commitment"), commitment);
    clearCache("commitment");
  } catch (err: any) {
    console.error("Firestore saveCommitmentContent failed:", err);
    throw err;
  }
}

export async function saveReviews(reviews: Review[]): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "reviews"), { reviews });
    clearCache("reviews");
  } catch (err: any) {
    console.error("Firestore saveReviews failed:", err);
    throw err;
  }
}

export async function saveAboutContent(content: AboutContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "about"), content);
    clearCache("about");
  } catch (err: any) {
    console.error("Firestore saveAboutContent failed:", err);
    throw err;
  }
}

// ——— Storage upload ———
export async function uploadImage(
  path: string,
  file: File
): Promise<string> {
  const { storage, ref, uploadBytes, getDownloadURL } = await getStorageService();
  const storageRef = ref(storage, path);

  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error("Timeout! Firebase Storage is not responding."));
    }, 15000);
  });

  try {
    await Promise.race([
      uploadBytes(storageRef, file),
      timeoutPromise
    ]);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (err) {
    console.error("Upload error in content.ts:", err);
    throw err;
  }
}

// ——— Write Order ———
export async function saveOrder(orderData: any): Promise<void> {
  try {
    const { db, collection, addDoc } = await getFirestoreService();
    await addDoc(collection(db, "orders"), {
      ...orderData,
      createdAt: new Date().toISOString()
    });
  } catch (err) {
    console.error("Failed to save order to Firebase:", err);
  }
}

export async function getOrders(): Promise<any[]> {
  try {
    const { db, collection, getDocs } = await getFirestoreService();
    const snap = await getDocs(collection(db, "orders"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return list;
  } catch {
    return [];
  }
}

export async function deleteOrder(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "orders", id));
  } catch (err) {
    console.error("Firestore deleteOrder failed:", err);
    throw err;
  }
}

// ——— Write Dealer Applications ———
export async function saveDealerApplication(applicationData: any): Promise<void> {
  try {
    const { db, collection, addDoc } = await getFirestoreService();
    await addDoc(collection(db, "dealerApplications"), {
      ...applicationData,
      createdAt: Date.now()
    });
  } catch (err) {
    console.error("Failed to save dealer application to Firebase:", err);
    throw err;
  }
}

export async function getDealerApplications(): Promise<any[]> {
  try {
    const { db, collection, getDocs } = await getFirestoreService();
    const snap = await getDocs(collection(db, "dealerApplications"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => b.createdAt - a.createdAt);
    return list;
  } catch {
    return [];
  }
}

export async function deleteDealerApplication(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "dealerApplications", id));
  } catch (err) {
    console.error("Firestore deleteDealerApplication failed:", err);
    throw err;
  }
}

// ——— Write Sales Rep Applications ———
export async function saveSalesRepApplication(applicationData: any): Promise<void> {
  try {
    const { db, collection, addDoc } = await getFirestoreService();
    await addDoc(collection(db, "salesRepApplications"), {
      ...applicationData,
      createdAt: Date.now()
    });
  } catch (err) {
    console.error("Failed to save sales rep application to Firebase:", err);
    throw err;
  }
}

export async function getSalesRepApplications(): Promise<any[]> {
  try {
    const { db, collection, getDocs } = await getFirestoreService();
    const snap = await getDocs(collection(db, "salesRepApplications"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => b.createdAt - a.createdAt);
    return list;
  } catch {
    return [];
  }
}

export async function deleteSalesRepApplication(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "salesRepApplications", id));
  } catch (err) {
    console.error("Firestore deleteSalesRepApplication failed:", err);
    throw err;
  }
}
